<template>
  <LydiaShortageModal
    v-model="showLydiaShortageModal"
    @confirm="handleModalConfirm"
    @charge="handleModalCharge"
  />

  <div class="daily-container flex-container" :class="{ loading: isLoading }">
    <h1 class="daily-title" :class="{ 'loading-title': isLoading }">
      실시간 운괘
    </h1>

    <!-- API 응답 도착 후 결과 표시 -->
    <div class="daily-content" v-if="hexagramData && !isLoading">
      <p class="daily-text">{{ hexagramData }}</p>
    </div>

    <!-- API 응답 대기 중일 때 HexagramShow 컴포넌트 표시 -->
    <div class="hexagram-text" v-else>
      <p>
        지금 운괘(卦)를 해석하고 있는 중입니다.
        <br />
        (5초~10초 소요됩니다.)
        <br />
        괘상을 감상하시면서 기다려주세요.
      </p>
      <HexagramShow />
    </div>

    <!-- 썸네일 항상 표시 -->
    <div v-if="hexagramData" style="text-align: center; margin-top: 1.5rem">
      <img
        :src="`${downloadBaseURL}/getThumb/${encodeURIComponent(hexagramName)}`"
        alt="썸네일"
        style="max-width: 80%; margin-bottom: 0.5rem"
      />

      <!-- 괘 다운로드 버튼 (조건부 노출) -->
      <!--div class="download-link-wrapper">
        <a
          v-if="canDownload"
          @click.prevent="downloadGua"
          class="download-link"
        >
          괘 다운로드({{ downloadPrice }}LYD)
        </a>
      </div-->
    </div>

    <div class="button-area" v-if="hexagramData && !isLoading">
      <button @click="goHome">돌아가기</button>
    </div>
  </div>
  <div v-if="isDownloading" class="spinner-wrapper">
    <div v-if="!downloadComplete" class="spinner"></div>
    <div v-else class="download-complete-message">{{ downloadResult }}</div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { GraphQLClient, gql } from 'graphql-request'
  import { useStore } from 'vuex'
  import { canUseThis, deductLydiaForMenu } from '@/utils/checkLydia'
  import LydiaShortageModal from '@/components/LydiaShortageModal.vue'
  import HexagramShow from '@/components/HexagramShow.vue' // HexagramShow 컴포넌트 임포트
  import { fullHexagrams } from '@/data/fullHexagrams.js'

  const store = useStore()
  const router = useRouter()

  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT
  const hexagramData = ref(null)

  const hexagramName = ref('') // 괘 이름을 저장할 변수

  const isLoading = ref(false) // 로딩 상태 변수 추가
  const graphQLClient = new GraphQLClient(apiEndpoint)

  const GET_HEXAGRAM_OF_THE_DAY_QUERY = gql`
    query getHexagramOfTheDay($guaIdx: Int!, $hyoIdx: Int!, $birth: String) {
      getHexagramOfTheDay(guaIdx: $guaIdx, hyoIdx: $hyoIdx, birth: $birth) {
        interpretation
      }
    }
  `

  // Lydia 부족 팝업 상태
  const showLydiaShortageModal = ref(false)

  // 모달 확인 버튼 클릭 핸들러
  const handleModalConfirm = () => {
    router.go(-1) // '확인' 클릭 시 IChingDaily로 이동
  }
  // 모달 충전 버튼 클릭 핸들러
  const handleModalCharge = () => {
    router.replace('/ChargeLydia') // '충전' 클릭 시 ChargeLydia로 이동 (가정)
  }

  onMounted(async () => {
    console.log('user: ', store.getters.getUser)

    const canProceed = await canUseThis('hexagramToday', store) // canUseThis 호출
    if (canProceed) {
      isLoading.value = true // API 요청 시작 전 로딩 상태 true

      const now = Date.now()
      const guaIdx = now % 64
      const hyoIdx = now % 6
      hexagramName.value = fullHexagrams[guaIdx].name_ko

      const variables = {
        guaIdx,
        hyoIdx,
        birth: store.getters.getUser.birthDate, // 실제 사용자 생년월일로 대체해야 합니다.
      }

      try {
        console.log('Fetching hexagram of the day..')
        const data = await graphQLClient.request(
          GET_HEXAGRAM_OF_THE_DAY_QUERY,
          variables,
        )
        hexagramData.value = data.getHexagramOfTheDay.interpretation

        isLoading.value = false // 응답 성공 시 로딩 상태 false

        await deductLydiaForMenu('hexagramToday', store) // Lydia 차감
      } catch (error) {
        console.error('Error fetching hexagram data:', error)
        // 에러 처리 로직 추가 (예: 사용자에게 메시지 표시)
        hexagramData.value = '괘 정보를 불러오는 데 실패했습니다.' // 에러 시 메시지 표시
        isLoading.value = false // 에러 발생 시 로딩 상태 false
      }
      console.log('Service available. Proceeding with HexagramToday.')
    } else {
      // canUseThis가 false를 반환하면 팝업 표시 및 라우팅
      showLydiaShortageModal.value = true
      console.log('Service unavailable. Showing Lydia Shortage Modal.')
      hexagramData.value = "서비스에 필요한 'Lydia'가 부족합니다." // 부족 메시지도 임시 변수에 저장
      isLoading.value = false // 서비스 이용 불가 시 로딩 상태 false
    }
  })

  const isDownloading = ref(false) // 다운로드 중 상태 변수 추가
  const downloadComplete = ref(false) // 다운로드 완료 상태 변수 추가
  const downloadResult = ref('') // 다운로드 결과 메시지 변수

  const downloadPrice = parseInt(import.meta.env.VITE_CHARGE_DOWNLOAD_GUA)
  const canDownload = computed(() => {
    return store.getters.getLydia >= downloadPrice
  })

  const downloadBaseURL = import.meta.env.VITE_DOWNLOAD_ENDPOINT
  const downloadGua = async () => {
    if (isDownloading.value) return // 이미 다운로드 중이면 중복 실행 방지

    isDownloading.value = true // 다운로드 시작 시 로딩 상태 true
    downloadComplete.value = false // 다운로드 완료 상태 초기화

    const ok = await canUseThis('downloadGua', store)
    if (!ok) {
      downloadResult.value = '서버 연결에 문제가 있습니다. 다시 시도해주세요.'
      downloadComplete.value = true
      setTimeout(() => {
        isDownloading.value = false
        downloadComplete.value = false
      }, 2000) // 텍스트 2초간 보여주기
      return
    }

    try {
      const response = await fetch(
        `${downloadBaseURL}/getGua/${encodeURIComponent(hexagramName.value)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken: localStorage.getItem('accessToken'),
          }),
        },
      )

      if (!response.ok) {
        throw new Error('서버 응답 오류')
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${hexagramName.value}.png`
      a.click()
      URL.revokeObjectURL(url)

      downloadResult.value = '다운로드가 완료되었습니다.'

      await deductLydiaForMenu('downloadGua', store) // Lydia 차감

      downloadComplete.value = true
      setTimeout(() => {
        isDownloading.value = false
        downloadComplete.value = false
      }, 1000) // 텍스트 2초간 보여주기
    } catch (err) {
      downloadResult.value = `다운로드 중 오류가 발생했습니다.(${err.message})`
      downloadComplete.value = true
      setTimeout(() => {
        isDownloading.value = false
        downloadComplete.value = false
      }, 2000) // 텍스트 2초간 보여주기
      //await store.dispatch('refundLydia', 'downloadGua') // 다운로드 실패 시 샤용 금액 환불
    }
  }

  function goHome() {
    router.go(-1)
  }
</script>

<style scoped>
  .flex-container {
    /* HexagramShow 컴포넌트가 중앙에 오도록 flex 설정 추가 */
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .daily-container {
    /* 기존 스타일 유지 */
    padding: 1.0rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    box-sizing: border-box;
    transition: background-color 0.3s ease; /* 배경색 전환 효과 추가 */
  }
  /* 배경이 검정색으로 전환되는 상태 */
  .daily-container.loading {
    background-color: black;
  }

  .daily-title {
    font-size: var(--font-size-14);
    text-align: center;
    margin-top: 0.5rem;
    font-weight: bold;
    transition: color 0.3s ease; /* 타이틀 색상 전환 효과 추가 */
  }
  /* 타이틀 텍스트가 흰색으로 전환되는 상태 */
  .daily-title.loading-title {
    color: white;
  }

  .daily-content {
    flex: 1;
    padding: 0.5rem;
    font-size: var(--font-size-10);
    line-height: 1.6;
    overflow-y: auto;
    white-space: pre-wrap;
    align-self: center;
    width: 90%;
  }

  .hexagram-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    text-align: center;
    position: relative;
    background-color: black;
  }

  .hexagram-text p {
    font-size: var(--font-size-10);
    color: white;
    align-self: flex-start; /* ✅ 상단 정렬 */
    margin-bottom: 10rem;
  }

  .hexagram-text :deep(.hexagram-wrapper) {
    margin-top: auto;
    margin-bottom: auto;
  }

  .daily-text {
    word-break: break-word;
    text-align: justify;
  }

  .daily-loading {
    flex: 1;
    padding: 1rem;
    font-size: var(--font-size-095);
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .download-area {
    text-align: center;
    margin-top: 2rem;
  }

  .gua-thumbnail {
    width: 180px;
    height: auto;
    margin-bottom: 1rem;
  }

  .download-link-wrapper {
    margin-bottom: 2.5rem; /* 돌아가기 버튼과 간격 확보 */
  }

  .download-link {
    color: #1a73e8;
    font-weight: bold;
    cursor: pointer;
  }
  .download-link:hover {
    text-decoration: underline;
  }

  .button-area {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #f44336;
    color: white;
    cursor: pointer;
  }

  button:hover {
    background-color: #da190b;
  }

  .spinner-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  .spinner {
    border: 6px solid #ccc;
    border-top: 6px solid #4caf50;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .download-complete-message {
    font-size: var(--font-size-12);
    color: white;
  }
</style>
